// Pure helpers for building the Tasks calendar month grid.

export const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

export const getFirstDayOfMonth = (year, month) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
};

export const formatDateKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function buildCalendarData({ calendarMonth, tasks, exchangeEvents, serialNumbers }) {
  const base = calendarMonth instanceof Date && !isNaN(calendarMonth) ? calendarMonth : new Date();
  const year = base.getFullYear();
  const month = base.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  // Build events map by date
  const eventsByDate = {};

  // Add tasks with due dates
  tasks.forEach((task) => {
    if (task.due_date) {
      const key = task.due_date.substring(0, 10);
      if (!eventsByDate[key]) eventsByDate[key] = [];
      eventsByDate[key].push({
        type: 'task',
        title: task.title,
        priority: task.priority,
        status: task.status,
        id: task.id,
      });
    }
  });

  // Add Exchange events (de-duplicated by id)
  if (Array.isArray(exchangeEvents)) {
    const seenEventIds = new Set();
    exchangeEvents.forEach((event) => {
      if (event.start) {
        if (event.id) {
          if (seenEventIds.has(event.id)) return;
          seenEventIds.add(event.id);
        }
        const key = event.start.substring(0, 10);
        if (!eventsByDate[key]) eventsByDate[key] = [];
        eventsByDate[key].push({
          type: 'exchange',
          title: event.subject,
          subject: event.subject,
          location: event.location,
          id: event.id,
          start: event.start,
          end: event.end,
          body: event.body || "",
          attendees: event.attendees || [],
          calendarOwner: event.calendar_owner,
          isOwn: event.is_own,
        });
      }
    });
  }

  // Add serial number expirations
  if (Array.isArray(serialNumbers)) {
    serialNumbers.forEach((sn) => {
      if (sn.expiry_date) {
        const key = sn.expiry_date.substring(0, 10);
        if (!eventsByDate[key]) eventsByDate[key] = [];
        eventsByDate[key].push({
          type: 'serial',
          title: sn.serial_number,
          product: sn.product_name,
          customer: sn.customer_name,
          id: `sn-${sn.id || Math.random()}`,
        });
      }
    });
  }

  const today = new Date();
  const todayKey = formatDateKey(today);

  const calendarDays = [];

  // Empty cells for days before first day of month
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push({ key: `empty-${year}-${month}-${i}`, isEmpty: true });
  }

  // Days of month
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    calendarDays.push({
      key: `day-${dateKey}`,
      day,
      dateKey,
      events: eventsByDate[dateKey] || [],
      isToday: dateKey === todayKey,
    });
  }

  return {
    year,
    month,
    calendarDays,
    monthLabel: base.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
  };
}
